import { Component } from '@angular/core';
import { HeadMetaComponent } from '@app/components/ui.component';
import { HelpButtonComponent } from '@app/components/help-button/help-button.component'
import { Device, DeviceCreate } from '@app/models/device.interface';
import { DeviceHelperService } from '@app/services/helpers/device-helper.service';
import { LetDirective } from '@ngrx/component';
import { Observable } from 'rxjs';
import { LoadingErrorBlockComponent } from "@app/components/loading-error-block/loading-error-block.component";
import { InputFieldComponent } from '@app/components/input-field/input-field.component';
import { DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AuthService } from '@app/services/auth.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { 
  faCirclePlus,
  faArrowLeft, 
  faSquareXmark,
  faSquareCheck,
  faFileImport,
 } from '@fortawesome/free-solid-svg-icons';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Provider } from '@app/models/provider.interface';
import { ProviderHelperService } from '@app/services/helpers/provider-helper.service';
import { DeviceStatus } from '@app/models/device-status.interface';
import { DeviceStatusHelperService } from '@app/services/helpers/device-status-helper.service';
import { Version } from '@app/models/version.interface';
import { VersionHelperService } from '@app/services/helpers/version-helper.service';

interface DeviceImport {
  serial_number: string,
  primary_pcb: string,
  sensor: string,
  slope_e_test: number,
  baseline_voltage: number,
  baseline_degradation_threshold: number
}
@Component({
    selector: 'app-devices',
    templateUrl: './listing.page.html',
    styleUrl: './listing.page.scss',
    imports: [
        HeadMetaComponent,
        LetDirective,
        LoadingErrorBlockComponent,
        DatePipe,
        FontAwesomeModule,
        RouterModule,
        ReactiveFormsModule,
        HelpButtonComponent,
    ]
})

export class DevicesPage {
  public title = 'Devices';
  searchForm: FormGroup = new FormGroup({
    serial_number: new FormControl<string>(''),
    provider_id: new FormControl<number>(0),
    device_status_id: new FormControl<number>(0),
  });
  importForm: FormGroup = new FormGroup({
    provider_id: new FormControl<number>(0),
    device_status_id: new FormControl<number>(0),
    version_id: new FormControl<number>(0),
    active: new FormControl<number>(1),
    pin: new FormControl<string>(''),
  });
  devices$: Observable<Device[]>;
  providers$: Observable<Provider[]>;
  deviceStatuses$: Observable<DeviceStatus[]>;
  versions$: Observable<Version[]>;
  
  faCirclePlus = faCirclePlus;
  faArrowLeft = faArrowLeft;
  faSquareXmark = faSquareXmark;
  faSquareCheck = faSquareCheck;
  faFileImport = faFileImport;

  public import_file: File = new File([], "");
  public devices: DeviceImport[] = [];

  constructor(
    private deviceHelper: DeviceHelperService,
    private providerHelper: ProviderHelperService,
    private deviceStatusHelper: DeviceStatusHelperService,
    private versionHelper: VersionHelperService,
    public auth: AuthService,
  ) {
    this.devices$ = this.deviceHelper.getAll();
    this.providers$ = this.providerHelper.getAll();
    this.deviceStatuses$ = this.deviceStatusHelper.getAll();
    this.versions$ = this.versionHelper.getAll();
  }

  public import(){
    const form = this.importForm.value;
    let output:DeviceCreate[] = [];
    this.devices.forEach(function(currentValue, index, arr) {
      output.push({...JSON.parse(JSON.stringify(currentValue)), ...form})
    });
    this.deviceHelper.createMany(output);
  }

  public importTemplate():string{
      return this.jsonToCsv(
        [], 
        [
          'serial_number',
          'primary_pcb',
          'sensor',
          'slope_e_test',
          'baseline_voltage',
          'baseline_degradation_threshold',
        ],
        'ArborsenseDeviceImportTemplate'
      )
    }

  private jsonToCsv(items: any, headers: string[], filename?: string){
    var replacer = function(key:any, value:any) { return value === null ? '' : value } 
    var csv = items.map(function(row:any){
      return headers.map(function(fieldName){
        return JSON.stringify(row[fieldName], replacer)
      }).join(',')
    });
    csv.unshift(headers.join(','));
    csv = csv.join('\r\n');
    return this.createCsv(csv, filename);
  }

  private createCsv(csv: any, filename?: string) {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const anchorElement = document.createElement('a');
    document.body.appendChild(anchorElement);
    anchorElement.style.display = 'none';
    anchorElement.href = url;
    if(filename) {
      anchorElement.download = filename;
    }
    anchorElement.click();

    window.URL.revokeObjectURL(url);
    return csv;
  }

  onFileChange(event:any) {
    this.import_file = event.target.files[0];
    const file: File = event.target.files[0];
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const csv: string = e.target.result;
      this.devices = this.csvToJson(csv);
    };
    reader.readAsText(file);
  }

  private csvToJson(csv: string): any[] {
    const lines: string[] = csv.split('\n');
    const headers: string[] = lines[0].trim().split(',');
    const result: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const obj: { [key: string]: string } = {};
      if(lines[i].trim().length > 0) {
        const currentLine: string[] = lines[i].trim().split(',');

        for (let j = 0; j < headers.length; j++) {
          obj[headers[j]] = currentLine[j];
        }
        result.push(obj);
      }
    }
    return result;
  }

  public submit(){
    const form = this.searchForm.value;
    if(form) {
      this.devices$ = this.deviceHelper.search(form.serial_number, form.provider_id, form.device_status_id);
    }
  }

  showProvider(devices: Device[]) {
    return devices.some((x) => x.provider != null);
  }
}
